import { FC } from 'react';
import { Field, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import {
  ComponentProps,
  DynamicHeading,
  getContrastThemeClass,
  resolveColorProperty,
  withTheme,
} from '@gn-audio/jss-components';

import { FirstButtonSettings, SecondButtonSettings } from 'common/types/buttonTemplateTypes';
import { usePlaceholderContainer } from 'components/Product/KSP/Placeholder/PlaceholderContainerContext';
import { DualButtonBlock } from 'composables/shared/DualButtonBlock/DualButtonBlock';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

export type TextSpotFields = {
  headline: Field<string>;
  contentFontSize: Field<number>;
  centerText: Field<boolean>;
  content: Field<string>;
  backgroundColor: Field<string>;
  isMainHeading: Field<boolean>;
  textFullWidth?: Field<boolean>;
  firstCtaButtonTrackingName?: Field<string>;
  secondCtaButtonTrackingName?: Field<string>;
  trackingSpotName?: Field<string>;
} & FirstButtonSettings &
  SecondButtonSettings;

export type TextSpotProps = {
  fields: TextSpotFields;
} & ComponentProps;

const DEFAULT_CONTENT_FONT_SIZE = 18;

const TextSpotBase: FC<TextSpotProps> = ({ fields }) => {
  const {
    backgroundColor,
    centerText,
    headline,
    content,
    contentFontSize,
    isMainHeading,
    textFullWidth,
    firstBtnText,
    firstBtnLink,
    firstBtnIcon,
    firstBtnVariant,
    firstBtnSize,
    firstCtaButtonTrackingName,
    secondBtnText,
    secondBtnLink,
    secondBtnIcon,
    secondBtnVariant,
    secondBtnSize,
    secondCtaButtonTrackingName,
    trackingSpotName,
  } = fields;

  const spotRef = useSpotTracking(trackingSpotName?.value);
  const { isInsidePlaceholder } = usePlaceholderContainer();

  const hasCustomBackground = !!backgroundColor?.value;
  const backgroundColorHex = resolveColorProperty(backgroundColor?.value?.toLowerCase());

  const containerClass = classNames(
    { 'bg-theme': !hasCustomBackground && !isInsidePlaceholder },
    { 'bg-transparent': isInsidePlaceholder && !backgroundColorHex }
  );

  const insidePlaceholderTheme = backgroundColorHex
    ? getContrastThemeClass(backgroundColorHex)
    : undefined;

  const contentContainerClass = textFullWidth?.value
    ? 'container'
    : 'container flex max-w-6xl justify-center';

  return (
    <div
      ref={spotRef}
      data-wt-spot={trackingSpotName?.value}
      className={classNames(containerClass, insidePlaceholderTheme)}
      style={backgroundColorHex ? { backgroundColor: backgroundColorHex } : undefined}
    >
      <div className={contentContainerClass}>
        <div
          className={classNames('flex w-full flex-col gap-3', centerText?.value && 'text-center')}
        >
          {headline?.value && (
            <DynamicHeading
              className={classNames(isMainHeading?.value ? 'heading-1' : 'heading-2')}
              isMainHeading={isMainHeading?.value}
              htmlContent={sanitize(headline.value)}
            />
          )}
          {content?.value && (
            <div
              style={{
                fontSize: `${contentFontSize?.value || DEFAULT_CONTENT_FONT_SIZE}px`,
              }}
              dangerouslySetInnerHTML={{
                __html: sanitize(content.value, {
                  ADD_ATTR: ['target', 'rel'],
                  FORBID_ATTR: ['onclick', 'onload', 'onerror'],
                }),
              }}
            />
          )}
          <DualButtonBlock
            firstBtnText={firstBtnText}
            firstBtnLink={firstBtnLink}
            firstBtnIcon={firstBtnIcon}
            firstBtnVariant={firstBtnVariant}
            firstBtnSize={firstBtnSize}
            firstCtaButtonTrackingName={firstCtaButtonTrackingName}
            secondBtnText={secondBtnText}
            secondBtnLink={secondBtnLink}
            secondBtnIcon={secondBtnIcon}
            secondBtnVariant={secondBtnVariant}
            secondBtnSize={secondBtnSize}
            secondCtaButtonTrackingName={secondCtaButtonTrackingName}
            className="mt-4 justify-center"
          />
        </div>
      </div>
    </div>
  );
};

export const TextSpot: FC<TextSpotProps> = (props) => {
  const { isInsidePlaceholder } = usePlaceholderContainer();

  const ThemedComponent = withTheme({ legacyBackgroundFieldName: 'backgroundColor' })(
    withDatasourceCheck()<TextSpotProps>(TextSpotBase)
  );

  const UnthemedComponent = withDatasourceCheck()<TextSpotProps>(TextSpotBase);

  return isInsidePlaceholder ? <UnthemedComponent {...props} /> : <ThemedComponent {...props} />;
};

export default TextSpot;
